import { useEffect, useState } from "react";
import { CustomModal, BtnState } from "../../components";
import InternalTransfer from "./internal";
import ExternalTransfer from "./external";
import { SnapSelectMenuOption } from "@snap-mobile/snap-ui/dist/types/utils";
import { transferError } from "../../types/errors";
import { useAppContext } from "../../hooks/use-context";
import {
  SpendAccountLimitsResponse,
  useSpendOrganizationAccountLimitsLazyQuery,
} from "graphql/generated";
import { useMutation } from "@apollo/client";
import {
  ORG_ACCOUNT_TRANSFER,
  ORG_EXTERNAL_TRANSFER,
} from "graphql/mutations/organization";
import { GET_ORGANIZATION_ACCOUNTS } from "graphql/queries/organizations";
import { FormatMoney, ParseMoney } from "helpers/format-money";
import { GROUP_EXTERNAL_TRANSFER } from "graphql/mutations/group";

type TransferModalProps = {
  isOpen: boolean;
  toggle: () => void;
};

function TransferModal({ isOpen, toggle }: TransferModalProps) {
  const { organization, group, toast } = useAppContext();
  const activeTabStyle = "border-b-2 border-gray-800";
  const tabOptions = ["Internal", "External"];

  const [accountLimits, setAccountLimits] =
    useState<SpendAccountLimitsResponse>();
  const [selectedTab, setSelectedTab] = useState(0);
  const [to, setTo] = useState<SnapSelectMenuOption[]>([]);
  const [from, setFrom] = useState<SnapSelectMenuOption[]>([]);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [btnState, setBtnState] = useState<BtnState>(BtnState.BASE);
  const [feeAmount, setFeeAmount] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [direction, setDirection] = useState("");
  const [externalTo, setExternalTo] = useState("");
  const [externalFrom, setExternalFrom] = useState("");
  const [selectedItem, setSelectedItem] = useState<
    SnapSelectMenuOption | undefined
  >();
  const [hasErrorObj, setHasErrorObj] = useState<transferError>({
    toError: false,
    fromError: false,
    noteError: false,
    amountError: false,
    availableError: false,
    macthingError: false,
  });

  const [
    getAccountLimits,
    { loading: loadingOrgAccountLimits, data: orgAccountLimitsData },
  ] = useSpendOrganizationAccountLimitsLazyQuery();

  const [
    internalTransfer,
    {
      loading: loadingInteralTransfer,
      data: internalTransferData,
      error: internalTransferError,
    },
  ] = useMutation(ORG_ACCOUNT_TRANSFER, {
    refetchQueries: [
      "SpendGroupsFiltered",
      "SpendTransactionsCompletedWhered",
      "SpendTransactionsPendingWhered",
      "SpendOrganizationAccounts",
    ],
    fetchPolicy: "network-only",
  });

  const [
    extOrgTransfer,
    {
      loading: loadingExtOrgTransfer,
      data: extOrgTransferData,
      error: extOrgTransferError,
    },
  ] = useMutation(ORG_EXTERNAL_TRANSFER, {
    refetchQueries: [
      "SpendGroupsFiltered",
      { query: GET_ORGANIZATION_ACCOUNTS, fetchPolicy: "network-only" },
    ],
    fetchPolicy: "network-only",
  });

  const [
    extGroupTransfer,
    {
      loading: loadingExtGroupTransfer,
      data: extGroupTransferData,
      error: extGroupTransferError,
    },
  ] = useMutation(GROUP_EXTERNAL_TRANSFER, {
    refetchQueries: [
      "SpendGroupsFiltered",
      { query: GET_ORGANIZATION_ACCOUNTS, fetchPolicy: "network-only" },
    ],
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    handleInitOptions();
    setAmount("");
    setNote("");
    // eslint-disable-next-line
  }, [isOpen, selectedTab]);

  useEffect(() => {
    getAccountLimits({
      variables: {
        groupId: group?.is(selectedItem?.value ?? "")
          ? selectedItem?.value
          : undefined,
      },
    });
  }, [selectedItem?.value]);

  useEffect(() => {
    if (
      !loadingOrgAccountLimits &&
      orgAccountLimitsData?.spendOrganizationAccountLimits
    ) {
      setAccountLimits(orgAccountLimitsData.spendOrganizationAccountLimits);
    }
  }, [orgAccountLimitsData, loadingOrgAccountLimits]);

  useEffect(() => {
    if (
      !loadingInteralTransfer &&
      internalTransferError &&
      internalTransferError.message
    ) {
      setHasErrorObj({ ...hasErrorObj, availableError: true });
      toast?.setToast({
        title: "Unable to Transfer Funds",
        message: internalTransferError?.message ?? "",
        type: "danger",
      });
      setBtnState(BtnState.BASE);
    } else if (
      !loadingInteralTransfer &&
      internalTransferData &&
      internalTransferData?.spendOrganizationAccountTransfer?.id
    ) {
      let selectedToItem = to.filter((op) => op.selected);
      let selectFromItem = from.filter((op) => op.selected);
      toast?.setToast({
        message: `${FormatMoney(
          Number(amount) * 100
        )} was Successfully Transfered to ${selectedToItem.at(0)?.name} from ${
          selectFromItem.at(0)?.name
        }`,
        type: "success",
      });
      handleModalReset();
    } else if (
      !loadingInteralTransfer &&
      internalTransferData &&
      internalTransferData?.spendOrganizationAccountTransfer?.approvalId
    ) {
      let selectedToItem = to.filter((op) => op.selected);
      let selectFromItem = from.filter((op) => op.selected);
      toast?.setToast({
        message: `Transfer from ${selectFromItem.at(0)?.name} to ${
          selectedToItem.at(0)?.name
        } has been submitted for approval.`,
        type: "success",
      });
      handleModalReset();
    }
    // eslint-disable-next-line
  }, [loadingInteralTransfer, internalTransferData, internalTransferError]);

  useEffect(() => {
    if (!loadingExtOrgTransfer && extOrgTransferData) {
      handleSuccessSubmitResults(
        extOrgTransferData?.spendOrganizationExternalTransfer?.id,
        extOrgTransferData?.spendOrganizationExternalTransfer?.status
      );
    }

    if (
      !loadingExtOrgTransfer &&
      extOrgTransferError &&
      extOrgTransferError.message
    ) {
      setHasErrorObj({ ...hasErrorObj, availableError: true });
      toast?.setToast({
        title: "Unable to Transfer Funds",
        message: extOrgTransferError?.message ?? "",
        type: "danger",
      });
      setBtnState(BtnState.BASE);
    }
    // eslint-disable-next-line
  }, [loadingExtOrgTransfer, extOrgTransferData, extOrgTransferError]);
  useEffect(() => {
    if (!loadingExtGroupTransfer && extGroupTransferData) {
      handleSuccessSubmitResults(
        extGroupTransferData?.spendGroupExternalTransfer?.id,
        extGroupTransferData?.spendGroupExternalTransfer?.status
      );
    }

    if (
      !loadingExtGroupTransfer &&
      extGroupTransferError &&
      extGroupTransferError.message
    ) {
      setHasErrorObj({ ...hasErrorObj, availableError: true });
      toast?.setToast({
        title: "Unable to Transfer Funds",
        message: extGroupTransferError?.message ?? "",
        type: "danger",
      });
      setBtnState(BtnState.BASE);
    }
    // eslint-disable-next-line
  }, [loadingExtGroupTransfer, extGroupTransferData, extGroupTransferError]);

  const handleSuccessSubmitResults = (transferId?: string, status?: string) => {
    const amountPennies = Number(amount) * 100;
    const feePennies = Number(feeAmount) * 100;
    if (transferId) {
      const endMessage =
        feePennies > 0
          ? ` which includes a fee of ${FormatMoney(feePennies)}.`
          : `.`;
      toast?.setToast({
        message: `Transfer of ${FormatMoney(
          amountPennies
        )} was initiated to ${externalTo} from ${externalFrom}${endMessage}`,
        type: "success",
      });
    }
    if (status === "approval created") {
      toast?.setToast({
        message: `Approval submitted for the amount of ${FormatMoney(
          amountPennies
        )} to be transfered to ${externalTo} from ${externalFrom}`,
        type: "success",
      });
    }
    setAmount("");
    setNote("");
    handleInitOptions();
    setHasErrorObj({
      toError: false,
      fromError: false,
      noteError: false,
      amountError: false,
      availableError: false,
      macthingError: false,
    });
    toggle();
    setBtnState(BtnState.BASE);
  };

  const handleModalReset = () => {
    setAmount("");
    setNote("");
    setHasErrorObj({
      toError: false,
      fromError: false,
      noteError: false,
      amountError: false,
      availableError: false,
      macthingError: false,
    });
    handleInitOptions();
    toggle();
    setBtnState(BtnState.BASE);
  };

  const handleInitOptions = () => {
    let programOption: SnapSelectMenuOption = {
      name:
        organization?.organization?.nickname ||
        organization?.organization?.legalName ||
        "",
      selected: false,
      value: organization?.organization?.id ?? "",
    };
    let groupOptions = organization?.getGroupMenuItems();
    setTo([programOption, ...(groupOptions ?? [])]);
    setFrom([programOption, ...(groupOptions ?? [])]);
  };

  const handleSelection = (type: string, options: SnapSelectMenuOption[]) => {
    if (type === "to") {
      setTo(options);
      setHasErrorObj({ ...hasErrorObj, toError: false, macthingError: false });
    }
    if (type === "from") {
      setFrom(options);
      setHasErrorObj({
        ...hasErrorObj,
        fromError: false,
        macthingError: false,
      });
    }
  };

  const handleTransfer = () => {
    let selectedToItem = to.filter((op) => op.selected);
    let selectFromItem = from.filter((op) => op.selected);
    if (selectedToItem.length === 0) {
      hasErrorObj.toError = true;
      setHasErrorObj({ ...hasErrorObj, toError: true });
    }
    if (selectFromItem.length === 0) {
      hasErrorObj.fromError = true;
      setHasErrorObj({ ...hasErrorObj, fromError: true });
    }
    if (amount === "0.00" || amount === "") {
      hasErrorObj.amountError = true;
      setHasErrorObj({ ...hasErrorObj, amountError: true });
    }
    if (note === "") {
      hasErrorObj.noteError = true;
      setHasErrorObj({ ...hasErrorObj, noteError: true });
    }
    let { amountError, fromError, noteError, toError } = hasErrorObj;
    if (amountError || fromError || noteError || toError) {
      toast?.setToast({
        message: "Please fill in all the input fields",
        type: "danger",
      });
      setBtnState(BtnState.BASE);
    } else if (selectedToItem.at(0)?.name === selectFromItem.at(0)?.name) {
      toast?.setToast({
        type: "danger",
        message: "To and From can not be the same accounts",
      });
      setHasErrorObj({ ...hasErrorObj, macthingError: true });
    } else if (note.length >= 81) {
      toast?.setToast({
        type: "danger",
        message: "Note can not be longer than 80 characters",
      });
      setBtnState(BtnState.BASE);
    } else {
      internalTransfer({
        variables: {
          input: {
            amount: ParseMoney(amount),
            description: note,
            toGroupIdOrOrgId: selectedToItem[0].value,
            fromGroupIdOrOrgId: selectFromItem[0].value,
          },
        },
        fetchPolicy: "network-only",
      });
    }
  };

  const handleExternalTransfer = () => {
    if (amount === "0.00" || amount === "") {
      setHasErrorObj({ ...hasErrorObj, amountError: true });
      hasErrorObj.amountError = true;
    }
    if (note === "") {
      setHasErrorObj({ ...hasErrorObj, noteError: true });
      hasErrorObj.noteError = true;
    }
    let { amountError, noteError } = hasErrorObj;

    if (!selectedItem?.value) {
      toast?.setToast({
        message: "Please select an account to begin.",
        type: "danger",
      });
      setBtnState(BtnState.BASE);
    } else if (amountError || noteError) {
      toast?.setToast({
        message: "Please fill in all the input fields",
        type: "danger",
      });
      setBtnState(BtnState.BASE);
    } else if (note.length >= 81) {
      toast?.setToast({
        type: "danger",
        message: "Note must not be longer than 80 character",
      });
      setBtnState(BtnState.BASE);
    } else {
      if (selectedItem?.value && organization?.is(selectedItem?.value)) {
        extOrgTransfer({
          variables: {
            input: {
              accountId: selectedAccountId,
              amount: ParseMoney(amount),
              direction,
              note,
            },
          },
        });
      } else if (selectedItem?.value && group?.is(selectedItem?.value)) {
        extGroupTransfer({
          variables: {
            input: {
              accountId: selectedAccountId,
              amount: ParseMoney(amount),
              direction,
              id: selectedItem.value,
              note,
            },
          },
        });
      }
    }
  };

  return (
    <CustomModal
      isOpen={isOpen}
      toggle={toggle}
      title={"Transfer"}
      btn1={{
        text: "Submit",
        btnStyle: "primary",
        onClick: () => {
          setBtnState(BtnState.LOADING);
          if (selectedTab === 0) {
            handleTransfer();
          } else {
            handleExternalTransfer();
          }
        },
        btnState,
      }}
      btn2={{
        text: "Cancel",
        btnStyle: "tertiary",
        onClick: () => {},
      }}
      customStyle="lg:w-[1082px] lg:mt-[20px] max-h-screen overflow-y-scroll"
      customOverflow="overflow-visible"
    >
      <>
        <div className="bg-white grid grid-cols-2 text-center lg:flex lg:justify-start border-b-2">
          {tabOptions.map((tab, idx) => {
            return (
              <p
                key={idx}
                className={`py-3 text-sm font-medium cursor-pointer ml-0 lg:ml-8 ${
                  tabOptions[selectedTab] === tab
                    ? activeTabStyle
                    : "text-gray-500"
                }`}
                onClick={() => {
                  setSelectedTab(idx);
                }}
              >
                {tab} <span className="hidden lg:inline">Transfer</span>
              </p>
            );
          })}
        </div>
        {
          {
            0: (
              <InternalTransfer
                isLoading={false}
                to={to}
                from={from}
                handleSelection={handleSelection}
                amount={amount}
                setAmount={setAmount}
                setNote={setNote}
                hasErrorObj={hasErrorObj}
                setHasErrorObj={setHasErrorObj}
              />
            ),
            1: (
              <ExternalTransfer
                to={to}
                amount={amount}
                hasErrorObj={hasErrorObj}
                feeAmount={feeAmount}
                setAmount={setAmount}
                setNote={setNote}
                setSelectedAccountId={setSelectedAccountId}
                setDirection={setDirection}
                setHasErrorObj={setHasErrorObj}
                setExternalTo={setExternalTo}
                setExternalFrom={setExternalFrom}
                setFeeAmount={setFeeAmount}
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
                limits={accountLimits}
              />
            ),
          }[selectedTab]
        }
      </>
    </CustomModal>
  );
}

export default TransferModal;
